import { isEmpty, isNull, round } from "lodash";
import { v4 as uuid } from "uuid";
import {
  getAdjustedQty,
  getTierBasedItemValues,
} from "../../../utils/pogCalculationV2";
import { singleValue, getMissingValue, orderStatus } from "../../../utils";
import {
  resetItemFieldsOnSelection,
  recalculateItemValues,
  CALCULATION_FIELDS,
} from "./index";

export const prepareInitialData = (data = []) => {
  const updatedData = [];

  if (data.length) {
    for (let i = 0; i < data.length; i++) {
      const item = {
        ...data[i],
        Core: isEmpty(data[i]?.Core) ? "N" : data[i]?.Core,
      };
      let customAdjQty = false;
      const calculatedAdjQty = getAdjustedQty(
        item?.QtyMultiplier,
        item?.RecQty,
        getMissingValue(item),
        item?.BreakPackType
      );
      const adjQty = isNull(item?.AdjQty) ? calculatedAdjQty : item?.AdjQty;

      // Detects whether adj. qty is custom or not.
      if (item?.AdjQty !== calculatedAdjQty) {
        customAdjQty = true;
      }

      const { pogCost, adjCost, totalCost, tier } = getTierBasedItemValues(
        item,
        adjQty
      );
      updatedData.push({
        ...item,
        _id: uuid(),
        ItemDescription: item?.ItemDescription?.trim(),
        selection: false,
        virtualLoading: true,
        PogCost: pogCost,
        AdjQty: item?.SelectedItem === singleValue.yes ? adjQty : 0, // Implicitly mark field as 0 not from calculated values
        AdjCost: item?.SelectedItem === singleValue.yes ? totalCost : null, // Implicitly mark field as 0 not from calculated values
        ExtendedCost: item?.SelectedItem === singleValue.yes ? adjCost : null, // Implicitly mark field as 0 not from calculated values
        Tier: tier,
        EachItemCostAfterTier: pogCost,
        CustomAdjQty: customAdjQty,
        __original: {
          SelectedItem: item?.SelectedItem,
        },
      });
    }
    return updatedData;
  }
  return data;
};

export const checkItemsBeforeSave = (
  dispatch,
  showNotifier,
  isSelected = true,
  itemList = [],
  selections = [],
  originalList = [],
  isOrderRelatedAction = false
) => {
  let isValidSaveData = true;
  // If no 'Selected' field value preset in settings form.
  if (!isSelected) {
    for (let i = 0, len = itemList.length; i < len; i++) {
      const item = itemList[i] || {};
      if (selections.includes(item._id)) {
        if (
          isOrderRelatedAction &&
          [singleValue.no, singleValue.existing].includes(item?.SelectedItem) &&
          [singleValue.no, singleValue.existing].includes(
            item?.__original.SelectedItem
          )
        ) {
          isValidSaveData = false;
          dispatch(
            showNotifier({
              message:
                "Few checked items are not saved or marked as selected 'N'/'E'. Please verify 'Sel' field.",
              severity: "error",
            })
          );
          break;
        }
      }
    }
  }
  return isValidSaveData;
};

export const prepareSaveData = (
  urlParams,
  details,
  storeId,
  userDetails,
  items = [],
  selections = [],
  isItemListDept = false
) => {
  let updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    pogNumber: urlParams.pogId,
    userId: userDetails?.email,
    skuList: [],
  };

  if (items.length && selections.length) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i] || {};
      let updatedItem = { ...item };
      if (selections.includes(updatedItem?._id)) {
        const calculatedItemValues = recalculateItemValues(
          {
            ...item,
            AdjQty: updatedItem?.AdjQty,
            QtyMultiplier: !!details.qtyMultiplier
              ? details.qtyMultiplier
              : updatedItem?.QtyMultiplier,
            DiscMultiplier: !!details.discountMultiplier
              ? details.discountMultiplier
              : updatedItem?.DiscMultiplier,
            CoreDiscount: !isEmpty(details.coreDiscount)
              ? round(details.coreDiscount, 2)
              : updatedItem?.CoreDiscount,
            NonCoreDiscount: !isEmpty(details.nonCoreDiscount)
              ? round(details.nonCoreDiscount, 2)
              : updatedItem?.NonCoreDiscount,
          },
          "",
          !!details.qtyMultiplier ||
            [singleValue.no, singleValue.existing].includes(details.selected) || // If selected is not 'Y' then we need calculated reset values to save to db.
            [singleValue.no, singleValue.existing].includes(item?.SelectedItem), // from settings making item N to Y hence calc. all dynamic values
          true
        );

        // In case of department X send uid instead of SKU number to identify duplicate item.
        updatedItem = {
          skuNumber: isItemListDept
            ? updatedItem?.UID
            : updatedItem?.StoreItemId,
          model: updatedItem?.Model,
          upc: updatedItem?.UPC,
          qtyMultiplier: calculatedItemValues?.QtyMultiplier,
          discMultiplier: !!details.discountMultiplier
            ? details.discountMultiplier
            : updatedItem?.DiscMultiplier,
          coreDiscount: !isEmpty(details.coreDiscount)
            ? round(details.coreDiscount, 2)
            : updatedItem?.CoreDiscount,
          nonCoreDiscount: !isEmpty(details.nonCoreDiscount)
            ? round(details.nonCoreDiscount, 2)
            : updatedItem?.NonCoreDiscount,
          datingDays: !isEmpty(String(details.datingDays))
            ? details.datingDays
            : updatedItem.DatingDays,
          selectedItem: !isEmpty(details.selected)
            ? details.selected
            : updatedItem.SelectedItem,
          poNumber:
            calculatedItemValues?.AdjQty > 0 ? updatedItem?.PONumber : null,
          recQty: updatedItem?.RecQty,
          adjQty: calculatedItemValues?.AdjQty,
          rdcQty: updatedItem?.RdcQty,
          adjCost: calculatedItemValues?.AdjCost,
          extCost: calculatedItemValues?.ExtendedCost,
          storeQty: 0,
          releaseStatus: updatedItem?.ReleaseStatus,
          releaseDate:
            calculatedItemValues?.AdjQty > 0 ? updatedItem?.ReleaseDate : null,
          eachItemCostAfterTier: calculatedItemValues?.EachItemCostAfterTier, // Mandatory
          isMissing: updatedItem?.IsMissing,
          description: updatedItem?.ItemDescription,
          core: updatedItem?.Core === "N" ? "" : "Core",
        };

        // If item is saved for selected 'N'/'E' then reset values.
        if (
          ![updatedItem?.SelectedItem, updatedItem?.selectedItem].includes(
            singleValue.yes
          ) ||
          [singleValue.no, singleValue.existing].includes(details.selected)
        ) {
          updatedItem = {
            ...updatedItem,
            qtyMultiplier: 1,
            discMultiplier: 1,
            coreDiscount: 0,
            nonCoreDiscount: 0,
            datingDays: 0,
            releaseDate: null,
            poNumber: null,
            adjQty: 0,
            extCost: 0,
            adjCost: 0,
            eachItemCostAfterTier: 0,
          };
        }
        updatedDetails = {
          ...updatedDetails,
          skuList: [...updatedDetails.skuList, updatedItem],
        };

        if (selections.length === updatedDetails.skuList.length) {
          break;
        }
      }
    }
  }
  return updatedDetails;
};

export const postSaveUpdateData = (
  formData,
  items,
  originalData,
  selections = []
) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (selections?.includes(item?._id)) {
      if (!isEmpty(formData?.selected)) {
        item = { ...item, SelectedItem: formData?.selected };
        item = {
          ...resetItemFieldsOnSelection("SelectedItem", item, originalData),
        };
      }

      if (
        formData?.datingDays !== "" &&
        item.SelectedItem === singleValue.yes
      ) {
        item = { ...item, DatingDays: formData?.datingDays };
        item = {
          ...resetItemFieldsOnSelection("DatingDays", item, originalData),
        };
      }

      if (
        formData?.discountMultiplier !== "" &&
        item.SelectedItem === singleValue.yes
      ) {
        item = { ...item, DiscMultiplier: formData?.discountMultiplier };
        item = {
          ...resetItemFieldsOnSelection("DiscMultiplier", item, originalData),
        };
        item = CALCULATION_FIELDS.includes("DiscMultiplier")
          ? recalculateItemValues(item, "DiscMultiplier")
          : item;
      }

      if (
        formData?.qtyMultiplier !== "" &&
        item.SelectedItem === singleValue.yes
      ) {
        item = { ...item, QtyMultiplier: formData?.qtyMultiplier };
        item = {
          ...resetItemFieldsOnSelection("QtyMultiplier", item, originalData),
        };
        item = CALCULATION_FIELDS.includes("QtyMultiplier")
          ? recalculateItemValues(item, "QtyMultiplier")
          : item;
      }

      if (
        formData?.coreDiscount !== "" &&
        item.SelectedItem === singleValue.yes
      ) {
        item = { ...item, CoreDiscount: round(formData?.coreDiscount, 2) };
        item = {
          ...resetItemFieldsOnSelection("CoreDiscount", item, originalData),
        };
        item = CALCULATION_FIELDS.includes("CoreDiscount")
          ? recalculateItemValues(item, "CoreDiscount")
          : item;
      }

      if (
        formData?.nonCoreDiscount !== "" &&
        item.SelectedItem === singleValue.yes
      ) {
        item = {
          ...item,
          NonCoreDiscount: round(formData?.nonCoreDiscount, 2),
        };
        item = {
          ...resetItemFieldsOnSelection("NonCoreDiscount", item, originalData),
        };
        item = CALCULATION_FIELDS.includes("NonCoreDiscount")
          ? recalculateItemValues(item, "NonCoreDiscount")
          : item;
      }

      if (item?.AdjQty === 0) {
        item = {
          ...item,
          PONumber: null,
          ReleaseDate: null,
        };
      }

      updatedItems.push(item);
    }
  }
  return updatedItems;
};

export const prepareSaveDatesData = (
  urlParams,
  date,
  storeId,
  userId,
  selections = [],
  filteredData = [],
  isItemListDept = false
) => {
  // In case of department X send uid instead of SKU number to identify duplicate item.
  const selectedSKUs =
    filteredData
      ?.map((_item) => {
        if (selections?.includes(_item?._id)) {
          return isItemListDept ? _item?.UID : _item?.StoreItemId;
        }
        return null;
      })
      .filter((i) => !!i) ?? [];
  let updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    pogNumber: urlParams.pogId,
    userId,
    releaseDate: date,
    skuNumberList: selections.length ? selectedSKUs : [],
  };
  return updatedDetails;
};

export const postSaveDatesUpdateData = (
  date,
  items,
  originalData,
  selections = []
) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (selections?.includes(item?._id)) {
      if (date !== "" && item.SelectedItem === singleValue.yes) {
        item = { ...item, ReleaseDate: date };
        item = {
          ...resetItemFieldsOnSelection("ReleaseDate", item, originalData),
        };
      }
      updatedItems.push(item);
    }
  }
  return updatedItems;
};

export const prepareDeleteDatesData = (
  urlParams,
  storeId,
  userId,
  selections = [],
  filteredData = [],
  isItemListDept = false
) => {
  // In case of department X send uid instead of SKU number to identify duplicate item.
  const selectedSKUs =
    filteredData
      ?.map((_item) => {
        if (selections?.includes(_item?._id)) {
          return isItemListDept ? _item?.UID : _item?.StoreItemId;
        }
        return null;
      })
      .filter((i) => !!i) ?? [];
  let updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    PogNumber: urlParams.pogId,
    userId,
    skuNumberList: selections.length ? selectedSKUs : [],
  };
  return updatedDetails;
};

export const postDeleteDatesUpdateData = (
  items,
  originalData,
  selections = []
) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (selections?.includes(item?._id)) {
      if (item.SelectedItem === singleValue.yes) {
        item = { ...item, ReleaseDate: null };
        item = {
          ...resetItemFieldsOnSelection("ReleaseDate", item, originalData),
        };
      }
      updatedItems.push(item);
    }
  }
  return updatedItems;
};

export const saveUpdatedItems =
  (postSaveUpdatedItems, isCreatePO = false) =>
  (oldItemDetails) => {
    return oldItemDetails.map((item) => {
      const updatedItem = postSaveUpdatedItems.find(
        (updatedItem) => updatedItem?._id === item?._id
      );
      return updatedItem
        ? {
            ...updatedItem,
            selection: isCreatePO ? updatedItem.selection : false, // removing selections after saving data except Create PO.
          }
        : item;
    });
  };

export const prepareCreatePOData = (
  urlParams,
  storeId,
  formData,
  userId,
  selections = [],
  filteredData = [],
  isItemListDept = false
) => {
  // In case of department X send uid instead of SKU number to identify duplicate item.
  const selectedSKUs =
    filteredData
      ?.map((_item) => {
        if (selections?.includes(_item?._id)) {
          return isItemListDept ? _item?.UID : _item?.StoreItemId;
        }
        return null;
      })
      .filter((i) => !!i) ?? [];
  const updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    pogNumber: urlParams.pogId,
    poNumber: formData?.customPONumber,
    createPOBy:
      formData?.generatePOBy === "Department" ? "DEPT" : formData?.generatePOBy,
    userId,
    skuNumberList: selections.length ? selectedSKUs : [],
  };
  return updatedDetails;
};

export const postCreatePOUpdateData = (
  items,
  details = [],
  selections = []
) => {
  const preparedItemList = [];
  if (selections.length && details.length) {
    for (let i = 0, len = items.length; i < len; i++) {
      let item = items[i] || {};
      if (selections.includes(item?._id)) {
        const itemWithPoNumber = details.find(
          (i) =>
            i?.SkuNumber === item?.StoreItemId &&
            item?.ReleaseStatus !== orderStatus.released
        );
        preparedItemList.push({
          ...item,
          PONumber: itemWithPoNumber?.PONumber,
        });
      }
    }
  }
  return preparedItemList;
};

export const getReleasedItemsWithPONumberMappings = (itemList = []) => {
  let mappings = {};
  for (let i = 0, len = itemList.length; i < len; i++) {
    let item = itemList[i];
    if (item.ReleaseStatus === orderStatus.released || !!item?.PONumber) {
      mappings = {
        ...mappings,
        [item.StoreItemId]: {
          PONumber: mappings[item.StoreItemId]?.PONumber
            ? [...mappings[item.StoreItemId]?.PONumber, item.PONumber]
            : [item.PONumber],
        },
      };
    }
  }
  return mappings;
};

const validPO = (formData, mapping, _item) => {
  let isValidPO = true;
  if (!mapping || !formData?.generatePOBy) {
    return !isValidPO;
  }
  const INVALID_PO = {
    Department: "OS ITEMDPT",
    POG: "OS ITEM999",
    Custom: formData?.customPONumber ?? "",
  };
  //If same item is selected for same PO number, then skip.
  if (
    mapping?.PONumber?.includes(INVALID_PO[formData?.generatePOBy]) &&
    _item?.PONumber === INVALID_PO[formData?.generatePOBy]
  ) {
    return isValidPO;
  }
  if (mapping?.PONumber?.includes(INVALID_PO[formData?.generatePOBy])) {
    isValidPO = false;
  }
  return isValidPO;
};

export const checkNonReleasedDuplicateItems = (
  formData = {},
  releasedMappings = [],
  filteredData = [],
  selections = []
) => {
  let duplicateSKU = [];
  let isValidSelection = true;
  for (let i = 0, len = filteredData.length; i < len; i++) {
    const _item = filteredData[i];

    if (selections.length > 1) {
      if (selections.includes(_item?._id)) {
        duplicateSKU.push(_item?.StoreItemId);
      }

      if (new Set(duplicateSKU).size !== duplicateSKU.length) {
        isValidSelection = false;
        break;
      }
    }

    // Check if item is selected and its not part of released item mapping having same PO.
    if (
      !isEmpty(releasedMappings) &&
      selections.includes(_item?._id) &&
      !validPO(formData, releasedMappings[_item?.StoreItemId], _item)
    ) {
      isValidSelection = false;
      break;
    }
  }
  return isValidSelection;
};

export const prepareInlineSaveData = (
  urlParams,
  storeId,
  userDetails,
  item,
  isItemListDept = false
) => {
  let updatedDetails = {
    storeId,
    departmentLetter: urlParams.departmentLetter,
    pogNumber: urlParams.pogId,
    userId: userDetails?.email,
    skuList: [],
  };
  let updatedItem = { ...item };
  if (updatedItem.SelectedItem === singleValue.yes) {
    updatedItem = {
      skuNumber: isItemListDept ? updatedItem?.UID : updatedItem?.StoreItemId,
      model: updatedItem?.Model,
      upc: updatedItem?.UPC,
      qtyMultiplier: updatedItem?.QtyMultiplier,
      discMultiplier: updatedItem?.DiscMultiplier,
      coreDiscount: updatedItem?.CoreDiscount,
      nonCoreDiscount: updatedItem?.NonCoreDiscount,
      datingDays: updatedItem.DatingDays,
      selectedItem: updatedItem.SelectedItem,
      poNumber: updatedItem?.AdjQty > 0 ? updatedItem?.PONumber : null,
      recQty: updatedItem?.RecQty,
      adjQty: updatedItem?.AdjQty,
      rdcQty: updatedItem?.RdcQty,
      adjCost: updatedItem?.AdjCost,
      extCost: updatedItem?.ExtendedCost,
      storeQty: 0,
      releaseStatus: updatedItem?.ReleaseStatus,
      releaseDate: updatedItem?.AdjQty > 0 ? updatedItem?.ReleaseDate : null,
      eachItemCostAfterTier: updatedItem?.EachItemCostAfterTier,
      isMissing: updatedItem?.IsMissing,
      description: updatedItem?.ItemDescription,
      core: updatedItem?.Core === "N" ? "" : "Core",
    };
  } else {
    updatedItem = {
      skuNumber: isItemListDept ? updatedItem?.UID : updatedItem?.StoreItemId,
      model: updatedItem?.Model,
      upc: updatedItem?.UPC,
      selectedItem: updatedItem.SelectedItem,
      storeQty: 0,
      qtyMultiplier: 1,
      discMultiplier: 1,
      coreDiscount: 0,
      nonCoreDiscount: 0,
      datingDays: 0,
      releaseStatus: updatedItem?.ReleaseStatus,
      releaseDate: null,
      poNumber: "",
      adjQty: 0,
      extCost: 0,
      adjCost: 0,
      eachItemCostAfterTier: 0,
      isMissing: updatedItem?.IsMissing,
      description: updatedItem?.ItemDescription,
      core: updatedItem?.Core === "N" ? "" : "Core",
    };
  }

  updatedDetails = {
    ...updatedDetails,
    skuList: [{ ...updatedItem }],
  };

  return updatedDetails;
};

export const postInlineSaveUpdateData = (updateItem, items) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i++) {
    let item = items[i] || {};
    if (item?._id === updateItem?._id) {
      item = {
        ...updateItem,
        PONumber: updateItem?.AdjQty > 0 ? updateItem.PONumber : null,
        ReleaseDate: updateItem?.AdjQty > 0 ? updateItem.ReleaseDate : null,
        __original: {
          SelectedItem: updateItem?.SelectedItem,
        },
      };
    }
    updatedItems.push(item);
  }
  return updatedItems;
};
